import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { AppSettings, Service, ProjectCategory, CustomProjectStatus } from '../types';

interface SettingsContextType {
  settings: AppSettings;
  updateSettings: (settings: AppSettings) => void;
  services: Service[];
  loading: boolean;
  addService: (service: Omit<Service, 'id' | 'createdAt'>) => Promise<void>;
  updateService: (service: Service) => Promise<void>;
  deleteService: (id: string) => Promise<void>;
  getServiceById: (id: string) => Service | undefined;
  getDefaultService: () => Service | undefined;
  setDefaultService: (id: string) => Promise<void>;
  fetchServices: () => Promise<void>;
  categories: ProjectCategory[];
  addCategory: (category: Omit<ProjectCategory, 'id' | 'createdAt'>) => void;
  updateCategory: (category: ProjectCategory) => void;
  deleteCategory: (id: string) => void;
  getCategoryById: (id: string) => ProjectCategory | undefined;
  customProjectStatuses: CustomProjectStatus[];
  addCustomProjectStatus: (status: Omit<CustomProjectStatus, 'id' | 'createdAt'>) => void;
  updateCustomProjectStatus: (status: CustomProjectStatus) => void;
  deleteCustomProjectStatus: (id: string) => void;
  getCustomProjectStatusById: (id: string) => CustomProjectStatus | undefined;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

const DEFAULT_SETTINGS: AppSettings = {
  minimumHourlyRate: 50,
  defaultHourlyRate: 75,
  defaultTaxRate: 10,
  defaultDiscountRate: 0,
  companyInfo: {
    name: 'Your Company Name',
    address: 'Your Company Address',
    phone: 'Your Company Phone',
    email: 'Your Company Email',
    website: 'www.yourcompany.com'
  }
};

const DEFAULT_SERVICE: Service = {
  id: 'default-service',
  name: 'Standard Service',
  description: 'Regular hourly rate for standard services',
  hourlyRate: 75,
  isDefault: true,
  createdAt: new Date().toISOString()
};

const DEFAULT_CATEGORIES: ProjectCategory[] = [
  {
    id: 'general',
    name: 'General',
    color: '#6B7280',
    description: 'General projects and services',
    createdAt: new Date().toISOString()
  },
  {
    id: 'maintenance',
    name: 'Maintenance',
    color: '#F59E0B',
    description: 'Maintenance and repair work',
    createdAt: new Date().toISOString()
  },
  {
    id: 'installation',
    name: 'Installation',
    color: '#10B981',
    description: 'New installations and setups',
    createdAt: new Date().toISOString()
  },
  {
    id: 'emergency',
    name: 'Emergency',
    color: '#EF4444',
    description: 'Urgent emergency repairs',
    createdAt: new Date().toISOString()
  }
];

const DEFAULT_CUSTOM_PROJECT_STATUSES: CustomProjectStatus[] = [
  {
    id: 'upcoming',
    name: 'Upcoming',
    color: '#3B82F6',
    type: 'pending',
    isDefault: true,
    order: 1,
    description: 'Project is scheduled but not yet started',
    createdAt: new Date().toISOString()
  },
  {
    id: 'in-progress',
    name: 'In Progress',
    color: '#F59E0B',
    type: 'active',
    isDefault: true,
    order: 2,
    description: 'Project is currently being worked on',
    createdAt: new Date().toISOString()
  },
  {
    id: 'on-hold',
    name: 'On Hold',
    color: '#6B7280',
    type: 'on-hold',
    isDefault: true,
    order: 3,
    description: 'Project is temporarily paused',
    createdAt: new Date().toISOString()
  },
  {
    id: 'finished',
    name: 'Finished',
    color: '#10B981',
    type: 'completed',
    isDefault: true,
    order: 4,
    description: 'Project has been completed',
    createdAt: new Date().toISOString()
  }
];

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);

  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState<ProjectCategory[]>([]);

  const [customProjectStatuses, setCustomProjectStatuses] = useState<CustomProjectStatus[]>(() => {
    return [];
  });

  // Fetch data when authenticated and org is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchOrganizationSettings();
      fetchServices();
      fetchCategories();
      fetchCustomProjectStatuses();
    }
  }, [isAuthenticated, currentOrganization]);

  // Fetch organization-specific settings from database
  const fetchOrganizationSettings = async () => {
    if (!currentOrganization) return;

    try {
      console.log('📋 Fetching organization settings for:', currentOrganization.name);
      
      const { data, error } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', currentOrganization.id)
        .single();

      if (error) {
        console.error('Error fetching organization settings:', error);
        // Use default settings if error
        setSettings(DEFAULT_SETTINGS);
        return;
      }

      if (data && data.settings) {
        // Merge organization settings with defaults
        const orgSettings = {
          minimumHourlyRate: data.settings.minimumHourlyRate || DEFAULT_SETTINGS.minimumHourlyRate,
          defaultHourlyRate: data.settings.defaultHourlyRate || DEFAULT_SETTINGS.defaultHourlyRate,
          defaultTaxRate: data.settings.defaultTaxRate || DEFAULT_SETTINGS.defaultTaxRate,
          defaultDiscountRate: data.settings.defaultDiscountRate || DEFAULT_SETTINGS.defaultDiscountRate,
          companyInfo: {
            name: data.settings.companyInfo?.name || DEFAULT_SETTINGS.companyInfo.name,
            address: data.settings.companyInfo?.address || DEFAULT_SETTINGS.companyInfo.address,
            phone: data.settings.companyInfo?.phone || DEFAULT_SETTINGS.companyInfo.phone,
            email: data.settings.companyInfo?.email || DEFAULT_SETTINGS.companyInfo.email,
            website: data.settings.companyInfo?.website || DEFAULT_SETTINGS.companyInfo.website
          }
        };

        console.log('✅ Organization settings loaded for:', currentOrganization.name);
        console.log('📋 Company Info:', orgSettings.companyInfo);
        setSettings(orgSettings);
      } else {
        // No settings found, use defaults
        console.log('⚠️ No organization settings found in database, using defaults');
        console.log('💡 TIP: Organization owner should go to Settings → Company Information and save company details');
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (error) {
      console.error('Error in fetchOrganizationSettings:', error);
      setSettings(DEFAULT_SETTINGS);
    }
  };

  const fetchCategories = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('project_categories')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('name', { ascending: true })
        .limit(50); // OPTIMIZED: Categories are usually limited

      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }

      if (data && data.length > 0) {
        // Transform Supabase data to ProjectCategory format
        const transformedCategories: ProjectCategory[] = data.map(category => ({
          id: category.id,
          name: category.name,
          color: category.color,
          description: category.description || '',
          createdAt: category.created_at
        }));

        setCategories(transformedCategories);
      } else {
        // If no categories exist, create the default categories
        await createDefaultCategories();
      }
    } catch (error) {
      console.error('Error in fetchCategories:', error);
    }
  };

  const fetchCustomProjectStatuses = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('custom_project_statuses')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('order_index', { ascending: true })
        .limit(50); // OPTIMIZED: Statuses are usually limited

      if (error) {
        console.error('Error fetching custom project statuses:', error);
        return;
      }

      if (data && data.length > 0) {
        // Transform Supabase data to CustomProjectStatus format
        const transformedStatuses: CustomProjectStatus[] = data.map(status => ({
          id: status.id,
          name: status.name,
          color: status.color,
          type: status.type,
          isDefault: status.is_default || false,
          order: status.order_index,
          description: status.description || '',
          createdAt: status.created_at
        }));

        setCustomProjectStatuses(transformedStatuses);
      } else {
        // If no custom statuses exist, create the default statuses
        await createDefaultCustomProjectStatuses();
      }
    } catch (error) {
      console.error('Error in fetchCustomProjectStatuses:', error);
    }
  };

  const createDefaultCustomProjectStatuses = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('custom_project_statuses')
        .insert(DEFAULT_CUSTOM_PROJECT_STATUSES.map(status => ({
          organization_id: currentOrganization.id,
          name: status.name,
          color: status.color,
          type: status.type,
          is_default: status.isDefault,
          order_index: status.order,
          description: status.description
        })))
        .select();

      if (error) {
        console.error('Error creating default custom project statuses:', error);
        return;
      }

      if (data) {
        const newStatuses: CustomProjectStatus[] = data.map(status => ({
          id: status.id,
          name: status.name,
          color: status.color,
          type: status.type,
          isDefault: status.is_default || false,
          order: status.order_index,
          description: status.description || '',
          createdAt: status.created_at
        }));
        setCustomProjectStatuses(newStatuses);
      }
    } catch (error) {
      console.error('Error in createDefaultCustomProjectStatuses:', error);
    }
  };
  const createDefaultCategories = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('project_categories')
        .insert(DEFAULT_CATEGORIES.map(category => ({
          organization_id: currentOrganization.id,
          id: category.id,
          name: category.name,
          color: category.color,
          description: category.description
        })))
        .select();

      if (error) {
        console.error('Error creating default categories:', error);
        return;
      }

      if (data) {
        const newCategories: ProjectCategory[] = data.map(category => ({
          id: category.id,
          name: category.name,
          color: category.color,
          description: category.description || '',
          createdAt: category.created_at
        }));
        setCategories(newCategories);
      }
    } catch (error) {
      console.error('Error in createDefaultCategories:', error);
    }
  };
  const fetchServices = async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('name', { ascending: true })
        .limit(100); // OPTIMIZED: Prevent loading huge datasets

      if (error) {
        console.error('Error fetching services:', error);
        return;
      }

      if (data && data.length > 0) {
        // Transform Supabase data to Service format
        const transformedServices: Service[] = data.map(service => ({
          id: service.id,
          name: service.name,
          description: service.description || '',
          hourlyRate: parseFloat(service.hourly_rate) || 0,
          isDefault: service.is_default || false,
          createdAt: service.created_at
        }));

        setServices(transformedServices);
      } else {
        // If no services exist, create the default service
        await createDefaultService();
      }
    } catch (error) {
      console.error('Error in fetchServices:', error);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultService = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('services')
        .insert({
          organization_id: currentOrganization.id,
          name: DEFAULT_SERVICE.name,
          description: DEFAULT_SERVICE.description,
          hourly_rate: DEFAULT_SERVICE.hourlyRate,
          is_default: true
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating default service:', error);
        return;
      }

      if (data) {
        const newService: Service = {
          id: data.id,
          name: data.name,
          description: data.description || '',
          hourlyRate: parseFloat(data.hourly_rate) || 0,
          isDefault: data.is_default || false,
          createdAt: data.created_at
        };
        setServices([newService]);
      }
    } catch (error) {
      console.error('Error in createDefaultService:', error);
    }
  };

  const updateSettings = async (newSettings: AppSettings) => {
    if (!currentOrganization) {
      console.error('No organization selected');
      return;
    }

    try {
      console.log('💾 Saving organization settings for:', currentOrganization.name);
      
      // Save settings to database
      const { error } = await supabase
        .from('organizations')
        .update({
          settings: {
            minimumHourlyRate: newSettings.minimumHourlyRate,
            defaultHourlyRate: newSettings.defaultHourlyRate,
            defaultTaxRate: newSettings.defaultTaxRate,
            defaultDiscountRate: newSettings.defaultDiscountRate,
            companyInfo: {
              name: newSettings.companyInfo.name,
              address: newSettings.companyInfo.address,
              phone: newSettings.companyInfo.phone,
              email: newSettings.companyInfo.email,
              website: newSettings.companyInfo.website
            }
          }
        })
        .eq('id', currentOrganization.id);

      if (error) {
        console.error('Error updating organization settings:', error);
        throw error;
      }

      console.log('✅ Organization settings saved successfully');
      
      // Update local state
      setSettings(newSettings);
    } catch (error) {
      console.error('Error in updateSettings:', error);
      throw error;
    }
  };

  const addService = async (service: Omit<Service, 'id' | 'createdAt'>) => {
    if (!currentOrganization) return;

    try {
      // If this is marked as default, update other services first
      if (service.isDefault) {
        await supabase
          .from('services')
          .update({ is_default: false })
          .eq('organization_id', currentOrganization.id)
          .neq('id', ''); // Update all services in this org
      }

      const { data, error } = await supabase
        .from('services')
        .insert({
          organization_id: currentOrganization.id,
          name: service.name,
          description: service.description,
          hourly_rate: service.hourlyRate,
          is_default: service.isDefault || false
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding service:', error);
        throw error;
      }

      if (data) {
        const newService: Service = {
          id: data.id,
          name: data.name,
          description: data.description || '',
          hourlyRate: parseFloat(data.hourly_rate) || 0,
          isDefault: data.is_default ? true : false,
          createdAt: data.created_at
        };
        
        // Update local state
        if (newService.isDefault) {
          setServices(prev => [...prev.map(s => ({ ...s, isDefault: false })), newService]);
        } else {
          setServices(prev => [...prev, newService]);
        }
      }
    } catch (error) {
      console.error('Error in addService:', error);
      throw error;
    }
  };

  const updateService = async (updatedService: Service) => {
    try {
      // If this service is being set as default, update other services first
      if (updatedService.isDefault) {
        await supabase
          .from('services')
          .update({ is_default: false })
          .neq('id', updatedService.id);
      }

      const { error } = await supabase
        .from('services')
        .update({
          name: updatedService.name,
          description: updatedService.description,
          hourly_rate: updatedService.hourlyRate,
          is_default: updatedService.isDefault || false
        })
        .eq('id', updatedService.id);

      if (error) {
        console.error('Error updating service:', error);
        throw error;
      }

      // Update local state
      if (updatedService.isDefault) {
        setServices(prev => prev.map(service => 
          service.id === updatedService.id 
            ? updatedService 
            : { ...service, isDefault: false }
        ));
      } else {
        setServices(prev => 
          prev.map(service => 
            service.id === updatedService.id ? updatedService : service
          )
        );
      }
    } catch (error) {
      console.error('Error in updateService:', error);
      throw error;
    }
  };

  const deleteService = async (id: string) => {
    try {
      const serviceToDelete = services.find(s => s.id === id);
      
      // Don't allow deleting the default service
      if (serviceToDelete?.isDefault) {
        throw new Error("Cannot delete the default service. Set another service as default first.");
      }
      
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting service:', error);
        throw error;
      }

      setServices(prev => prev.filter(service => service.id !== id));
    } catch (error) {
      console.error('Error in deleteService:', error);
      throw error;
    }
  };

  const getServiceById = (id: string) => {
    return services.find(service => service.id === id);
  };

  const getDefaultService = () => {
    return services.find(service => service.isDefault);
  };

  const setDefaultService = async (id: string) => {
    try {
      // First, remove default from all services
      await supabase
        .from('services')
        .update({ is_default: false })
        .neq('id', ''); // Update all services

      // Then set the new default
      const { error } = await supabase
        .from('services')
        .update({ is_default: true })
        .eq('id', id);

      if (error) {
        console.error('Error setting default service:', error);
        throw error;
      }

      setServices(prev => prev.map(service => ({
        ...service,
        isDefault: service.id === id
      })));
    } catch (error) {
      console.error('Error in setDefaultService:', error);
      throw error;
    }
  };

  const addCategory = async (category: Omit<ProjectCategory, 'id' | 'createdAt'>) => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('project_categories')
        .insert({
          organization_id: currentOrganization.id,
          name: category.name,
          color: category.color,
          description: category.description
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding category:', error);
        throw error;
      }

      if (data) {
        const newCategory: ProjectCategory = {
          id: data.id,
          name: data.name,
          color: data.color,
          description: data.description || '',
          createdAt: data.created_at
        };
        setCategories(prev => [...prev, newCategory]);
      }
    } catch (error) {
      console.error('Error in addCategory:', error);
      throw error;
    }
  };

  const updateCategory = async (updatedCategory: ProjectCategory) => {
    try {
      const { error } = await supabase
        .from('project_categories')
        .update({
          name: updatedCategory.name,
          color: updatedCategory.color,
          description: updatedCategory.description
        })
        .eq('id', updatedCategory.id);

      if (error) {
        console.error('Error updating category:', error);
        throw error;
      }

      setCategories(prev => 
        prev.map(category => 
          category.id === updatedCategory.id ? updatedCategory : category
        )
      );
    } catch (error) {
      console.error('Error in updateCategory:', error);
      throw error;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      // Don't allow deleting the 'general' category
      if (id === 'general') {
        throw new Error("Cannot delete the 'General' category. This is the default category.");
      }
      
      const { error } = await supabase
        .from('project_categories')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting category:', error);
        throw error;
      }

      setCategories(prev => prev.filter(category => category.id !== id));
    } catch (error) {
      console.error('Error in deleteCategory:', error);
      throw error;
    }
  };

  const getCategoryById = (id: string) => {
    return categories.find(category => category.id === id);
  };

  const addCustomProjectStatus = async (status: Omit<CustomProjectStatus, 'id' | 'createdAt'>) => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('custom_project_statuses')
        .insert({
          organization_id: currentOrganization.id,
          name: status.name,
          color: status.color,
          type: status.type,
          is_default: status.isDefault || false,
          order_index: status.order,
          description: status.description
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding custom project status:', error);
        throw error;
      }

      if (data) {
        const newStatus: CustomProjectStatus = {
          id: data.id,
          name: data.name,
          color: data.color,
          type: data.type,
          isDefault: data.is_default || false,
          order: data.order_index,
          description: data.description || '',
          createdAt: data.created_at
        };
        setCustomProjectStatuses(prev => [...prev, newStatus]);
      }
    } catch (error) {
      console.error('Error in addCustomProjectStatus:', error);
      throw error;
    }
  };

  const updateCustomProjectStatus = async (updatedStatus: CustomProjectStatus) => {
    try {
      const { error } = await supabase
        .from('custom_project_statuses')
        .update({
          name: updatedStatus.name,
          color: updatedStatus.color,
          type: updatedStatus.type,
          is_default: updatedStatus.isDefault || false,
          order_index: updatedStatus.order,
          description: updatedStatus.description
        })
        .eq('id', updatedStatus.id);

      if (error) {
        console.error('Error updating custom project status:', error);
        throw error;
      }

      setCustomProjectStatuses(prev => 
        prev.map(status => 
          status.id === updatedStatus.id ? updatedStatus : status
        )
      );
    } catch (error) {
      console.error('Error in updateCustomProjectStatus:', error);
      throw error;
    }
  };

  const deleteCustomProjectStatus = async (id: string) => {
    try {
      const statusToDelete = customProjectStatuses.find(s => s.id === id);
      
      // Don't allow deleting default statuses
      if (statusToDelete?.isDefault) {
        throw new Error("Cannot delete default project statuses. These are required for the system to function properly.");
      }
      
      const { error } = await supabase
        .from('custom_project_statuses')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting custom project status:', error);
        throw error;
      }

      setCustomProjectStatuses(prev => prev.filter(status => status.id !== id));
    } catch (error) {
      console.error('Error in deleteCustomProjectStatus:', error);
      throw error;
    }
  };

  const getCustomProjectStatusById = (id: string) => {
    return customProjectStatuses.find(status => status.id === id);
  };

  return (
    <SettingsContext.Provider value={{
      settings,
      updateSettings,
      services,
      loading,
      addService,
      updateService,
      deleteService,
      getServiceById,
      getDefaultService,
      setDefaultService,
      fetchServices,
      categories,
      addCategory,
      updateCategory,
      deleteCategory,
      getCategoryById,
      customProjectStatuses,
      addCustomProjectStatus,
      updateCustomProjectStatus,
      deleteCustomProjectStatus,
      getCustomProjectStatusById
    }}>
      {children}
    </SettingsContext.Provider>
  );
};